import { useState, useEffect, useMemo } from 'react';
import {
    Box, Typography, Paper, TextField, CircularProgress,
    Alert, Chip, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Button, InputAdornment, TableSortLabel
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { FirestoreService } from '../../sdk/firestoreService';

const PLAN_LABELS = { trial: 'Trial', basic: 'נֵר', pro: 'מְנוֹרָה', community: 'מָאוֹר' };
const PLAN_COLORS = { trial: '#94a3b8', basic: '#2563eb', pro: '#7c3aed', community: '#059669' };
const PLAN_ORDER = { community: 0, pro: 1, basic: 2, trial: 3 };

const toDate = (val) => {
    if (!val) return null;
    const d = val?.toDate ? val.toDate() : new Date(val);
    return isNaN(d.getTime()) ? null : d;
};

const formatDate = (val) => {
    const d = toDate(val);
    if (!d) return '—';
    return d.toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const COLUMNS = [
    { id: 'name', label: 'שם הארגון' },
    { id: 'city', label: 'עיר' },
    { id: 'plan', label: 'תוכנית' },
    { id: 'screens', label: 'מסכים', align: 'center' },
    { id: 'createdAt', label: 'תאריך הרשמה' },
    { id: 'actions', label: 'פעולות', align: 'center', sortable: false },
];

const OrganizationsManager = () => {
    const [organizations, setOrganizations] = useState([]);
    const [licenses, setLicenses] = useState({});
    const [screenCounts, setScreenCounts] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState('createdAt');
    const [sortDir, setSortDir] = useState('desc');

    useEffect(() => {
        const load = async () => {
            try {
                const [orgs, lics, counts] = await Promise.all([
                    FirestoreService.getAllOrganizations(),
                    FirestoreService.getAllLicenses(),
                    FirestoreService.getScreensCountByOrg()
                ]);
                setOrganizations(orgs);
                setLicenses(lics);
                setScreenCounts(counts);
            } catch (e) {
                setError('שגיאה בטעינת ארגונים: ' + e.message);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, []);

    const handleMasquerade = (orgId) => {
        window.open(`/${orgId}/screens?masquerade=true`, '_blank');
    };

    const handleSort = (columnId) => {
        if (sortBy === columnId) {
            setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(columnId);
            setSortDir(columnId === 'createdAt' ? 'desc' : 'asc');
        }
    };

    const sortedFiltered = useMemo(() => {
        let list = organizations.filter(org => {
            if (!searchQuery) return true;
            const q = searchQuery.toLowerCase();
            const city = org.address?.city || org.city || '';
            return (org.name || '').toLowerCase().includes(q) ||
                   city.toLowerCase().includes(q) ||
                   (org.organizationId || '').toLowerCase().includes(q);
        });

        list.sort((a, b) => {
            let valA, valB;
            switch (sortBy) {
                case 'name':
                    valA = (a.name || '').toLowerCase();
                    valB = (b.name || '').toLowerCase();
                    break;
                case 'city':
                    valA = (a.address?.city || a.city || '').toLowerCase();
                    valB = (b.address?.city || b.city || '').toLowerCase();
                    break;
                case 'plan': {
                    const planA = licenses[a.organizationId]?.plan || '';
                    const planB = licenses[b.organizationId]?.plan || '';
                    valA = PLAN_ORDER[planA] ?? 99;
                    valB = PLAN_ORDER[planB] ?? 99;
                    break;
                }
                case 'screens':
                    valA = screenCounts[a.organizationId] || 0;
                    valB = screenCounts[b.organizationId] || 0;
                    break;
                case 'createdAt':
                    valA = toDate(licenses[a.organizationId]?.createdAt)?.getTime() || 0;
                    valB = toDate(licenses[b.organizationId]?.createdAt)?.getTime() || 0;
                    break;
                default:
                    return 0;
            }
            if (valA < valB) return sortDir === 'asc' ? -1 : 1;
            if (valA > valB) return sortDir === 'asc' ? 1 : -1;
            return 0;
        });

        return list;
    }, [organizations, licenses, searchQuery, sortBy, sortDir, screenCounts]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress sx={{ color: '#1e3a5f' }} />
            </Box>
        );
    }

    return (
        <Box>
            <Typography variant="h6" fontWeight={700} color="#1e3a5f" mb={2}>
                ארגונים ({organizations.length})
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <TextField
                fullWidth
                size="small"
                placeholder="חיפוש לפי שם, עיר או מזהה..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ mb: 2 }}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon sx={{ color: '#94a3b8' }} />
                        </InputAdornment>
                    )
                }}
            />

            <TableContainer component={Paper} sx={{ borderRadius: '12px' }}>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ bgcolor: '#f1f5f9' }}>
                            {COLUMNS.map(col => (
                                <TableCell
                                    key={col.id}
                                    align={col.align || 'right'}
                                    sx={{ fontWeight: 700, color: '#1e3a5f' }}
                                    sortDirection={sortBy === col.id ? sortDir : false}
                                >
                                    {col.sortable === false ? col.label : (
                                        <TableSortLabel
                                            active={sortBy === col.id}
                                            direction={sortBy === col.id ? sortDir : 'asc'}
                                            onClick={() => handleSort(col.id)}
                                            sx={{
                                                color: '#1e3a5f !important',
                                                '& .MuiTableSortLabel-icon': { color: '#1e3a5f !important' }
                                            }}
                                        >
                                            {col.label}
                                        </TableSortLabel>
                                    )}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sortedFiltered.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ py: 4, color: '#94a3b8' }}>
                                    {searchQuery ? 'לא נמצאו ארגונים תואמים' : 'אין ארגונים במערכת'}
                                </TableCell>
                            </TableRow>
                        ) : (
                            sortedFiltered.map(org => {
                                const license = licenses[org.organizationId];
                                const plan = license?.plan || '—';
                                const screens = screenCounts[org.organizationId] || 0;

                                return (
                                    <TableRow key={org.organizationId} hover>
                                        <TableCell>
                                            <Typography fontWeight={600} fontSize="0.875rem">
                                                {org.name || 'ללא שם'}
                                            </Typography>
                                            <Typography fontSize="0.7rem" color="#94a3b8">
                                                {org.organizationId.substring(0, 8)}...
                                            </Typography>
                                        </TableCell>
                                        <TableCell>{org.address?.city || org.city || '—'}</TableCell>
                                        <TableCell>
                                            {plan !== '—' ? (
                                                <Chip
                                                    label={PLAN_LABELS[plan] || plan}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: (PLAN_COLORS[plan] || '#94a3b8') + '18',
                                                        color: PLAN_COLORS[plan] || '#94a3b8',
                                                        fontWeight: 600,
                                                        fontSize: '0.75rem'
                                                    }}
                                                />
                                            ) : '—'}
                                        </TableCell>
                                        <TableCell align="center">{screens}</TableCell>
                                        <TableCell>{formatDate(license?.createdAt)}</TableCell>
                                        <TableCell align="center">
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                startIcon={<VisibilityIcon />}
                                                onClick={() => handleMasquerade(org.organizationId)}
                                                sx={{
                                                    borderColor: '#1e3a5f',
                                                    color: '#1e3a5f',
                                                    textTransform: 'none',
                                                    fontSize: '0.78rem',
                                                    '&:hover': { bgcolor: '#1e3a5f12' }
                                                }}
                                            >
                                                צפייה כארגון
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default OrganizationsManager;
